import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Activity, TrendingUp, Calendar, Plus } from 'lucide-react';
import { usePreview } from '../../context/PreviewContext';
import { Button } from '../../components/ui/button';
import { UNITS } from '../../utils/unitFormatter';
import BloodTrendChart from '../../components/shared/BloodTrendChart';
import TimeWindowSelector, { filterByTimeWindow } from '../../components/TimeWindowSelector';
import { formatChartDate } from '../../utils/dateUtils';

/**
 * Preview Dashboard Page
 * Read-only dashboard with mock data - matches real dashboard layout exactly
 */

// Mock mood data for preview
const MOCK_MOOD_DATA = [
  { date: '2024-12-01', score: 3 },
  { date: '2024-12-03', score: 4 },
  { date: '2024-12-05', score: 3 },
  { date: '2024-12-07', score: 4 },
  { date: '2024-12-09', score: 5 },
  { date: '2024-12-11', score: 4 },
  { date: '2024-12-13', score: 3 },
  { date: '2024-12-15', score: 4 },
  { date: '2024-12-17', score: 4 },
  { date: '2024-12-19', score: 5 },
  { date: '2024-12-21', score: 4 }
];

const MOOD_OPTIONS = [
  { score: 1, label: 'Very low', emoji: '😔' },
  { score: 2, label: 'Low', emoji: '😕' },
  { score: 3, label: 'Okay', emoji: '😐' },
  { score: 4, label: 'Good', emoji: '🙂' },
  { score: 5, label: 'Very good', emoji: '😊' },
];

// Preview Wellbeing Chart Component
const PreviewWellbeingChart = () => {
  const [timeRange, setTimeRange] = useState(30);
  const [selectedMood, setSelectedMood] = useState(4);
  
  const getMoodEmoji = (score) => {
    const emojis = { 1: '😔', 2: '😕', 3: '😐', 4: '🙂', 5: '😊' };
    return emojis[score] || '😐';
  };

  const formatDateLabel = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(month, 10) - 1]} ${parseInt(day, 10)}`;
  };

  const points = MOCK_MOOD_DATA.map((item, idx) => {
    const x = ((idx + 0.5) / MOCK_MOOD_DATA.length) * 100;
    const y = 100 - (item.score * 18) - 5;
    return { x, y, score: item.score, date: item.date };
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  return (
    <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 sm:p-6 lg:p-8">
      <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-3 text-center">
        How You've Been Feeling
      </h3>
      
      <div className="flex justify-center gap-2 sm:gap-3 mb-3">
        {MOOD_OPTIONS.map((mood) => {
          const isSelected = selectedMood === mood.score;
          return (
            <button
              key={mood.score}
              disabled
              className={`
                w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center
                bg-white border border-slate-200
                cursor-not-allowed opacity-80
                ${isSelected ? 'ring-2 ring-primary ring-offset-1 bg-primary/10 border-primary/30' : ''}
              `}
              title={`${mood.label} (Preview mode)`}
            >
              <span className="text-[24px] sm:text-[26px]">{mood.emoji}</span>
            </button>
          );
        })}
      </div>

      <div className="flex justify-center mb-4">
        <div className="flex gap-1 bg-white rounded-lg p-1 border border-slate-200 opacity-60">
          {[7, 30, 90].map((days) => (
            <button
              key={days}
              disabled
              className={`px-3 py-1 text-xs font-medium rounded-md cursor-not-allowed ${
                timeRange === days
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-400'
              }`}
            >
              {days}d
            </button>
          ))}
        </div>
      </div>

      <div className="relative w-full">
        <div className="absolute left-0 top-0 bottom-6 w-10 sm:w-12 flex flex-col justify-between text-[10px] sm:text-xs text-slate-400">
          <span>Higher</span>
          <span>Lower</span>
        </div>

        <div className="ml-10 sm:ml-12 mr-2 sm:mr-4">
          <div className="absolute left-10 sm:left-12 right-2 sm:right-4 h-28 sm:h-32 lg:h-36 flex flex-col justify-between pointer-events-none">
            {[1, 2, 3, 4, 5].map((level) => (
              <div key={level} className="border-b border-slate-200/50" style={{ height: '20%' }} />
            ))}
          </div>
          
          <div className="relative h-28 sm:h-32 lg:h-36">
            <svg 
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <path
                d={pathD}
                fill="none"
                stroke="#7fb3b3"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            
            <svg 
              className="absolute inset-0 w-full h-full pointer-events-none"
              style={{ overflow: 'visible' }}
            >
              {points.map((p, i) => (
                <circle
                  key={i}
                  cx={`${p.x}%`}
                  cy={`${p.y}%`}
                  r="5"
                  fill="#5a9a9a"
                />
              ))}
            </svg>
            
            <div className="absolute inset-0 flex">
              {MOCK_MOOD_DATA.map((item, idx) => (
                <div
                  key={item.date}
                  className="flex-1 group relative"
                >
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                    <div className="bg-slate-600 text-white text-xs rounded-lg px-2 py-1 whitespace-nowrap shadow-lg text-center mt-[-36px]">
                      <span className="text-base">{getMoodEmoji(item.score)}</span>
                      <div className="text-[10px] text-slate-300 mt-0.5">
                        {formatDateLabel(item.date)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-between mt-1 text-[10px] sm:text-xs text-slate-400">
            <span>Dec 1</span>
            <span>Dec 11</span>
            <span>Dec 21</span>
          </div>
        </div>
      </div>

      <p className="text-[10px] sm:text-xs text-slate-400 mt-3 text-center">
        This is not a medical assessment. There's no right or wrong — just patterns.
      </p>
    </div>
  );
};

/**
 * Compute dashboard domain based on time window
 * Dashboard ends at last data entry date (not today)
 */
const computeDashboardDomain = (results, country = 'AU', timeWindow = 'all') => {
  if (!results || results.length === 0) return null;
  
  const sortedResults = [...results].sort((a, b) => new Date(a.date) - new Date(b.date));
  const lastEntry = sortedResults[sortedResults.length - 1];
  const lastEntryDate = new Date(lastEntry.date);
  
  const endOfLastMonth = new Date(lastEntryDate.getFullYear(), lastEntryDate.getMonth() + 1, 0, 23, 59, 59, 999);
  const domainEndTs = endOfLastMonth.getTime();
  
  let domainStartTs;
  const monthsMap = { '12m': 12, '24m': 24, '36m': 36 };
  const months = monthsMap[timeWindow];
  
  if (months) {
    const startDate = new Date(endOfLastMonth);
    startDate.setMonth(startDate.getMonth() - months);
    startDate.setDate(1);
    startDate.setHours(0, 0, 0, 0);
    domainStartTs = startDate.getTime();
  } else {
    const firstEntry = sortedResults[0];
    const firstDate = new Date(firstEntry.date);
    const startOfFirstMonth = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1, 0, 0, 0, 0);
    domainStartTs = startOfFirstMonth.getTime();
  }
  
  return {
    domainStartTs,
    domainEndTs,
    formatDate: (timestamp) => formatChartDate(timestamp, country)
  };
};

const PreviewDashboardPage = () => {
  const { bloodResults, nutritionRecords, clinicalRanges } = usePreview();
  const [dashboardTimeWindow, setDashboardTimeWindow] = useState('all');
  const country = 'AU';

  // Filter results by time window
  const filteredBloodResults = useMemo(() => {
    return filterByTimeWindow(bloodResults, dashboardTimeWindow);
  }, [bloodResults, dashboardTimeWindow]);

  // Filter nutrition records by time window
  const filteredNutritionRecords = useMemo(() => {
    return filterByTimeWindow(nutritionRecords, dashboardTimeWindow);
  }, [nutritionRecords, dashboardTimeWindow]);

  // Compute domain for blood charts
  const dashboardDomain = useMemo(() => {
    return computeDashboardDomain(bloodResults, country, dashboardTimeWindow);
  }, [bloodResults, dashboardTimeWindow]);

  // Compute domain for nutrition charts
  const nutritionDomain = useMemo(() => {
    return computeDashboardDomain(nutritionRecords, country, dashboardTimeWindow);
  }, [nutritionRecords, dashboardTimeWindow]);

  const markers = [
    { key: 'wbc', label: 'WBC', color: '#0F766E', unit: clinicalRanges.wbc?.unit || UNITS.BLOOD_COUNT, range: clinicalRanges.wbc },
    { key: 'lymphocytes', label: 'Lymphocytes', color: '#059669', unit: clinicalRanges.lymphocytes?.unit || UNITS.BLOOD_COUNT, range: clinicalRanges.lymphocytes },
    { key: 'platelets', label: 'Platelets', color: '#0369A1', unit: clinicalRanges.platelets?.unit || UNITS.BLOOD_COUNT, range: clinicalRanges.platelets },
    { key: 'haemoglobin', label: 'Haemoglobin', color: '#D97706', unit: clinicalRanges.haemoglobin?.unit || UNITS.HEMOGLOBIN_SI, range: clinicalRanges.haemoglobin },
    { key: 'neutrophils', label: 'Neutrophils', color: '#BE123C', unit: clinicalRanges.neutrophils?.unit || UNITS.BLOOD_COUNT, range: clinicalRanges.neutrophils }
  ];

  // Nutrition markers
  const nutritionMarkers = [
    { key: 'vitamin_d', label: 'Vitamin D', color: '#F59E0B', unit: 'nmol/L', range: { min: 50, max: 250 } },
    { key: 'vitamin_b12', label: 'Vitamin B12', color: '#8B5CF6', unit: 'pmol/L', range: { min: 150, max: 700 } },
    { key: 'folate', label: 'Folate', color: '#10B981', unit: 'nmol/L', range: { min: 10, max: 45 } },
    { key: 'iron', label: 'Iron', color: '#EF4444', unit: 'µmol/L', range: { min: 10, max: 30 } },
    { key: 'ferritin', label: 'Ferritin', color: '#6366F1', unit: 'µg/L', range: { min: 30, max: 300 } }
  ];

  const latestResult = bloodResults[bloodResults.length - 1];

  // Sort markers by which have data
  const sortedMarkers = useMemo(() => {
    return [...markers].sort((a, b) => {
      const aHasData = filteredBloodResults.some(r => r[a.key] != null);
      const bHasData = filteredBloodResults.some(r => r[b.key] != null);
      if (aHasData && !bHasData) return -1;
      if (!aHasData && bHasData) return 1;
      return 0;
    });
  }, [filteredBloodResults]);

  // Sort nutrition markers by which have data
  const sortedNutritionMarkers = useMemo(() => {
    return [...nutritionMarkers].sort((a, b) => {
      const aHasData = filteredNutritionRecords.some(r => r[a.key] != null);
      const bHasData = filteredNutritionRecords.some(r => r[b.key] != null);
      if (aHasData && !bHasData) return -1;
      if (!aHasData && bHasData) return 1;
      return 0;
    });
  }, [filteredNutritionRecords]);

  // Render a single graph card (matching DashboardBloodGraphs)
  const renderGraph = (marker) => {
    const chartData = filteredBloodResults
      .map(r => ({
        date: r.date,
        timestamp: new Date(r.date).getTime(),
        value: r[marker.key]
      }))
      .filter(d => d.value != null)
      .sort((a, b) => a.timestamp - b.timestamp);

    const hasData = chartData.length > 0;

    return (
      <div key={marker.key} className="bg-white rounded-xl px-0 py-4 md:px-3 md:py-6 border border-slate-200">
        <div className="mb-3 md:mb-4 px-3 md:px-3">
          <h3 className="text-base md:text-lg font-semibold text-slate-900">{marker.label} Trend ({marker.unit})</h3>
          <p className="text-xs md:text-sm text-slate-500">
            Reference range: {marker.range.min}-{marker.range.max}
          </p>
        </div>
        
        {/* Chart Legend */}
        <div className="mb-3 px-3 md:px-3 flex items-center gap-4 text-xs text-slate-600">
          <div className="flex items-center gap-1.5">
            <div 
              className="w-4 h-3 rounded-sm" 
              style={{ backgroundColor: marker.color, opacity: 0.15 }}
            ></div>
            <span>Reference range</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div 
              className="w-4 h-0.5 rounded-full" 
              style={{ backgroundColor: marker.color }}
            ></div>
            <span>Your results</span>
          </div>
        </div>

        {/* Chart */}
        <BloodTrendChart
          data={chartData}
          markerConfig={marker}
          showLegend={false}
          pageDomain={dashboardDomain}
        />

        {!hasData && (
          <div className="mt-3 md:mt-4 text-center px-3 md:px-3">
            <p className="text-xs md:text-sm text-slate-600">No results recorded yet for this marker.</p>
          </div>
        )}
      </div>
    );
  };

  // Render a single nutrition graph card
  const renderNutritionGraph = (marker) => {
    const chartData = filteredNutritionRecords
      .map(r => ({
        date: r.date,
        timestamp: new Date(r.date).getTime(),
        value: r[marker.key]
      }))
      .filter(d => d.value != null)
      .sort((a, b) => a.timestamp - b.timestamp);

    const hasData = chartData.length > 0;

    return (
      <div key={marker.key} className="bg-white rounded-xl px-0 py-4 md:px-3 md:py-6 border border-emerald-200">
        <div className="mb-3 md:mb-4 px-3 md:px-3">
          <h3 className="text-base md:text-lg font-semibold text-slate-900">{marker.label} Trend ({marker.unit})</h3>
          <p className="text-xs md:text-sm text-slate-500">
            Reference range: {marker.range.min}-{marker.range.max}
          </p>
        </div>
        
        {/* Chart Legend */}
        <div className="mb-3 px-3 md:px-3 flex items-center gap-4 text-xs text-slate-600">
          <div className="flex items-center gap-1.5">
            <div 
              className="w-4 h-3 rounded-sm" 
              style={{ backgroundColor: marker.color, opacity: 0.15 }}
            ></div>
            <span>Reference range</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div 
              className="w-4 h-0.5 rounded-full" 
              style={{ backgroundColor: marker.color }}
            ></div>
            <span>Your results</span>
          </div>
        </div>

        {/* Chart */}
        <BloodTrendChart
          data={chartData}
          markerConfig={marker}
          showLegend={false}
          pageDomain={nutritionDomain}
        />

        {!hasData && (
          <div className="mt-3 md:mt-4 text-center px-3 md:px-3">
            <p className="text-xs md:text-sm text-slate-600">No results recorded yet for this marker.</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Your health overview at a glance</p>
        </div>
        <Button disabled className="opacity-50 cursor-not-allowed">
          <Plus className="h-4 w-4 mr-2" />
          Add Results
        </Button>
      </div>

      {/* Mood Tracker */}
      <PreviewWellbeingChart />

      {/* Stats Cards - matching real dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 md:gap-6">
        {markers.map((marker) => {
          const latestValue = latestResult?.[marker.key];
          
          const getStatus = (value, range) => {
            if (value === null || value === undefined || !range) return null;
            if (value < range.min) return { label: 'Low', color: 'text-blue-600', bg: 'bg-blue-50' };
            if (value > range.max) return { label: 'High', color: 'text-amber-600', bg: 'bg-amber-50' };
            return { label: 'Normal', color: 'text-green-600', bg: 'bg-green-50' };
          };
          
          const status = getStatus(latestValue, marker.range);
          
          return (
            <div
              key={marker.key}
              className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-1 sm:mb-2">
                <p className="text-[10px] sm:text-xs md:text-sm font-medium text-slate-500 uppercase tracking-wider truncate">{marker.label}</p>
                <Activity className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" style={{ color: marker.color }} />
              </div>
              <div className="flex items-baseline gap-1.5 sm:gap-2">
                <p className="text-lg sm:text-2xl md:text-3xl font-bold text-slate-900">
                  {latestValue !== null && latestValue !== undefined ? latestValue.toFixed(1) : '—'}
                </p>
                {status && (
                  <span className={`text-[9px] sm:text-[10px] font-semibold px-1.5 py-0.5 rounded ${status.bg} ${status.color}`}>
                    {status.label}
                  </span>
                )}
              </div>
              <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 sm:mt-1">Latest reading</p>
            </div>
          );
        })}
      </div>

      {/* Wrapper for Blood + Nutrition sections with shared sticky time selector on mobile */}
      <div className="relative">
        {/* Mobile: Sticky time selector that covers both Blood and Nutrition sections */}
        <div 
          className="lg:hidden sticky z-40 bg-white"
          style={{ top: '76px' }}
        >
          <div className="flex justify-end px-4 py-2.5 border-b border-slate-200 shadow-sm">
            <TimeWindowSelector selected={dashboardTimeWindow} onChange={setDashboardTimeWindow} />
          </div>
        </div>

        {/* Blood Results Section - ALL GRAPHS STACKED (matching real dashboard) */}
        {bloodResults.length > 0 && (
          <div className="bg-white rounded-2xl border-2 border-slate-200">
            {/* Mobile: Title only (selector moved to shared sticky above) */}
            <div className="lg:hidden px-4 pt-4 pb-2">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Blood Results</h2>
              <p className="text-xs text-slate-600">Core blood markers relevant to monitoring disease activity and overall blood health.</p>
            </div>
            
            {/* Desktop: Sticky header with title + selector side by side */}
            <div 
              className="hidden lg:block sticky z-40 bg-white rounded-t-2xl border-b border-slate-100"
              style={{ top: '0px' }}
            >
              <div className="px-6 pt-6 pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-3xl font-bold text-slate-900 mb-2">Blood Results</h2>
                    <p className="text-sm text-slate-600">Core blood markers relevant to monitoring disease activity and overall blood health.</p>
                  </div>
                  <div className="flex-shrink-0">
                    <TimeWindowSelector selected={dashboardTimeWindow} onChange={setDashboardTimeWindow} />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Charts - ALL STACKED */}
            <div className="-mx-4 md:mx-0 px-0 pb-4 md:px-3 md:pb-6">
              <div className="space-y-4 md:space-y-6">
                {sortedMarkers.map(marker => renderGraph(marker))}
              </div>
            </div>
          </div>
        )}

        {/* Nutrition & Deficiencies Section - ALL GRAPHS STACKED (matching real dashboard) */}
        {nutritionRecords.length > 0 && (
          <div className="bg-emerald-50 rounded-2xl border-2 border-emerald-200 mt-6">
            <div className="px-4 pt-4 md:px-6 md:pt-6 pb-2">
              <h2 className="text-2xl md:text-3xl font-bold text-emerald-900 mb-2">Nutrition & Deficiencies</h2>
              <p className="text-xs md:text-sm text-emerald-700">Supportive biomarkers influenced by diet, absorption, and supplementation.</p>
            </div>
            
            {/* Charts - ALL STACKED */}
            <div className="-mx-4 md:mx-0 px-0 pb-4 md:px-3 md:pb-6">
            <div className="space-y-4 md:space-y-6">
              {sortedNutritionMarkers.map(marker => renderNutritionGraph(marker))}
            </div>
          </div>
        </div>
      )}
      </div>
      {/* End of Blood + Nutrition wrapper */}

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/preview/blood-results"
          className="bg-white rounded-xl border border-slate-200 p-5 hover:border-teal-300 transition-colors"
        >
          <Activity className="h-6 w-6 text-teal-600 mb-2" />
          <h3 className="font-semibold text-slate-900">Blood Results</h3>
          <p className="text-sm text-slate-500 mt-1">Track your CBC and other markers</p>
        </Link>

        <Link
          to="/preview/nutrition"
          className="bg-white rounded-xl border border-slate-200 p-5 hover:border-amber-300 transition-colors"
        >
          <TrendingUp className="h-6 w-6 text-amber-600 mb-2" />
          <h3 className="font-semibold text-slate-900">Nutrition</h3>
          <p className="text-sm text-slate-500 mt-1">Monitor vitamins and minerals</p>
        </Link>

        <Link
          to="/preview/vitamins"
          className="bg-white rounded-xl border border-slate-200 p-5 hover:border-purple-300 transition-colors"
        >
          <Calendar className="h-6 w-6 text-purple-600 mb-2" />
          <h3 className="font-semibold text-slate-900">Treatments</h3>
          <p className="text-sm text-slate-500 mt-1">View your treatment timeline</p>
        </Link>
      </div>
    </div>
  );
};

export default PreviewDashboardPage;
